import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Pill } from 'ui-components/components/pill';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';

import messages from './messages';
import style from './style.module.less';
import { getUpsellItemName } from '../../../../../components/upsell-modal/helper';
import { emptyArray } from 'ui-components/default-props/empty';
import { useSellerFormattedDate } from '../../../../../hooks/use-datetime-formats';
import { sortPremiumFeatures } from '../../../../../hooks/use-seller-feature';
import { getPlanShipmentLimitMessage } from '../../hooks/use-plan-details';
import { messages as sharedMessages } from '../../messages';
import { localizedPlanNameMessageDescriptor } from '../plan-name-helper';
import { PriceValue } from '../price-value';
import { SupportValue } from '../support-value';

export const SimplifiedPlan = ({
  currentBillingPlanId,
  currentPeriodEndDateTimeUTC,
  subPlans = emptyArray,
  selectedPlanId,
  onSelectPlanClick = noop,
  hasPendingPlanChange = false,
  highlightedBillingPlanIds = emptyArray,
}) => {
  const formattedCurrentPeriodEndDateString = useSellerFormattedDate(currentPeriodEndDateTimeUTC);
  const intl = useIntl();
  // one of the sub plans are selected
  const selectedSubPlan = subPlans.find((p) => p.billingPlanId === selectedPlanId);
  // the sub plan that matches the current plan or recommended plan should be the one shown by default
  const defaultSubPlan = subPlans.find((p) => highlightedBillingPlanIds.includes(p.billingPlanId));
  // otherwise, show the first plan
  const firstPlan = subPlans[0];
  const planToDisplay = selectedSubPlan ?? defaultSubPlan ?? firstPlan;
  const columnOutlined = !!selectedSubPlan;
  const highlightedPlanIsCurrentPlan = highlightedBillingPlanIds.includes(currentBillingPlanId);

  // planName
  const { messageDescriptor, values } = localizedPlanNameMessageDescriptor(planToDisplay.simplifiedPlanCode);

  // shipment limit
  const shipmentLimit = getPlanShipmentLimitMessage(planToDisplay);

  // features
  const planIncludedFeatureIds = planToDisplay.subscriptionLineItems.reduce(
    (acc, lineItem) => (lineItem.isIncludedInPlan ? [...acc, ...lineItem.featureIds] : acc),
    [],
  );
  const orderedFeatureIds = sortPremiumFeatures(planIncludedFeatureIds);

  const shouldShowHighlightedAttribute = !!defaultSubPlan;

  return (
    <div className={style['plan-section']}>
      <div
        className={classnames(
          style['plan-attribute'],
          {
            [style.selected]: columnOutlined && shouldShowHighlightedAttribute,
          },
          { [style.highlighted]: shouldShowHighlightedAttribute },
        )}
      >
        {shouldShowHighlightedAttribute ? (
          <Text
            textType={TextType.Label}
            value={highlightedPlanIsCurrentPlan ? sharedMessages.currentPlan : sharedMessages.recommendedPlan}
          />
        ) : null}
      </div>
      <div
        aria-label={intl.formatMessage(messageDescriptor)}
        className={classnames(
          style['plan-container'],
          { [style.selected]: columnOutlined },
          { [style['selected-highlighted']]: shouldShowHighlightedAttribute },
        )}
        onClick={() => onSelectPlanClick(planToDisplay.billingPlanId)}
      >
        <div className={style['plan-header']}>
          <Text formattedMessageValues={values} textType={TextType.H4} value={messageDescriptor} />
          <Text className={style['price-value']} textType={TextType.H1} value={<PriceValue plan={planToDisplay} />} />
        </div>
        <div className={style['plan-info-section']}>
          {subPlans.length > 1 ? (
            <>
              <Text textType={TextType.Label} value={sharedMessages.monthlyShipmentsLabel} />
              <DropdownMenu className={style['shipment-limit-dropdown']} dropdownMenuShouldHaveWidthOfButton variant='form'>
                <DropdownMenuToggler className={style['shipment-dropdown-toggler']}>{shipmentLimit}</DropdownMenuToggler>
                {subPlans.map((internalPlan) => {
                  const isInternalPlanHighlighted = highlightedBillingPlanIds.includes(internalPlan.billingPlanId);
                  return (
                    <DropdownMenuItem
                      key={internalPlan.code}
                      className={style['shipment-limit-dropdown-item']}
                      onClick={() => onSelectPlanClick(internalPlan.billingPlanId)}
                      selected={isInternalPlanHighlighted}
                    >
                      {getPlanShipmentLimitMessage(internalPlan)}
                      {isInternalPlanHighlighted && highlightedPlanIsCurrentPlan ? (
                        <Pill
                          className={style['plan-name-pill']}
                          label={
                            hasPendingPlanChange
                              ? intl.formatMessage(messages.untilMessage, {
                                  date: formattedCurrentPeriodEndDateString,
                                })
                              : intl.formatMessage(sharedMessages.currentPlan)
                          }
                          theme='neutral'
                        />
                      ) : null}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenu>
            </>
          ) : (
            <div className={style['centered-shipment-limit']}>
              {subPlans[0].shipmentLimit.limit !== -1 ? (
                <Text
                  formattedMessageValues={{ numOfShipments: shipmentLimit }}
                  textType={TextType.Label}
                  value={messages.singularShipmentsMessage}
                />
              ) : (
                <Text textType={TextType.Label} value={messages.unlimitedShipmentsMessage} />
              )}
            </div>
          )}
        </div>
        <div className={style['plan-info-section']}>
          <Text
            formattedMessageValues={{
              number: planToDisplay?.userLimit?.limit ?? 0,
            }}
            textType={TextType.Paragraph}
            value={sharedMessages.numberOfUsers}
          />
          <Text
            formattedMessageValues={{
              supportTypes: <SupportValue plan={planToDisplay} />,
            }}
            textType={TextType.Paragraph}
            value={messages.supportMessage}
          />
          {planToDisplay?.hasBranding ? <Text textType={TextType.Paragraph} value={messages.brandingMessage} /> : null}
        </div>
        {orderedFeatureIds.length > 0 && (
          <div className={style['plan-info-section']}>
            <Text className={style['feature-label']} textType={TextType.Caption} value={messages.featuresLabel} />
            <ul className={style['feature-list']}>
              {orderedFeatureIds.reduce((acc, featureId) => {
                const featureMessageDescriptor = getUpsellItemName(featureId);
                if (featureMessageDescriptor) {
                  acc.push(
                    <li key={featureId}>
                      <Text className={style['feature-line-item']} textType={TextType.Paragraph} value={featureMessageDescriptor} />
                    </li>,
                  );
                }
                return acc;
              }, [])}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
